/**
 * dealerMenuConfig.js — Jun-2026 (zentrale Navigations-Konfiguration).
 *
 * Single Source of Truth für die Händler-Navigation. Vier Oberflächen leiten
 * sich hieraus ab und bleiben dadurch automatisch synchron:
 *   1. Desktop-Header-Dropdown  (Header.jsx)
 *   2. Mobile-Menü (Flat-Liste + Schnellzugriff-Kacheln)  (Header.jsx)
 *   3. Desktop-Dashboard-Schnellzugriff-Leiste  (DealerQuickAccessBar.jsx)
 *   4. Globale Command-Palette (Strg/⌘+K)  (CommandPalette.jsx)
 *
 * Verhalten/Reihenfolge/Test-IDs sind identisch zur vorherigen hartcodierten
 * Variante — wer hier etwas ändert, ändert es überall.
 */
import {
    Truck, ClipboardCheck, Wrench, FileText, Receipt, ScanLine, Camera, Zap,
    Package, Store, Building2, Plus, Settings as SettingsIcon, Calculator, Landmark, RotateCcw, PhoneIncoming, Handshake,
    Users, Bike, UserPlus, BookText, LayoutDashboard, BarChart3, Mail, Mailbox, MailWarning, BadgeEuro, Archive, Video, Eye, MessagesSquare, Send, FolderOpen,
} from "lucide-react";

/** Erzeugt den Onlineshop-Slug eines Händlers (Firmenname → kebab-case, sonst id). */
export const dealerSlug = (user) =>
    (user && user.company_name
        ? user.company_name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 60) || user.id
        : (user ? user.id : ""));

/**
 * Schnellzugriff-Kacheln (mit Live-Badges).
 * - Mobile-Menü nutzt die Kacheln OHNE `dashOnly` (testid `mobile-quick-{key}`).
 * - Desktop-Dashboard-Leiste nutzt ALLE (testid `dash-quick-{key}`).
 * `badgeKey` referenziert ein Feld aus `useDealerQuickCounts`.
 */
export const DEALER_QUICK_TILES = [
    { key: "disposition", label: "Disposition", icon: Truck, path: "/dealer/disposition" },
    { key: "inventur", label: "Inventur", icon: ClipboardCheck, path: "/dealer/fahrzeuginventur?filter=uncounted", badgeKey: "inventory_uncounted" },
    { key: "werkstatt", label: "Werkstatt", icon: Wrench, path: "/dealer/workshop-orders?status=open", badgeKey: "workshop_open" },
    { key: "barcode", label: "Barcode", icon: ScanLine, path: "/dealer/wms?action=scan-barcode" },
    { key: "invoices", label: "Rechnungen", icon: FileText, path: "/dealer/invoices", dashOnly: true },
    { key: "direct-sale", label: "Direktverkauf", icon: Receipt, path: "/dealer/invoices?action=direct-sale", dashOnly: true },
];

/**
 * Desktop-Header-Dropdown-Struktur (B2B-Händler).
 * `kind: "item"`  → einzelner Eintrag.
 * `kind: "sub"`   → Hover-Flyout; das ERSTE `items`-Element ist die „Übersicht"
 *                   (im Mobile-Menü wird daraus der nicht eingerückte Parent).
 * `onlineshop: true` → Pfad wird zur Laufzeit aus dem Händler-Slug gebaut.
 */
export const DEALER_MENU_SECTIONS = [
    {
        kind: "sub", label: "Dashboard", icon: LayoutDashboard, triggerTestid: "menu-dashboard-section", subWidth: "w-56",
        items: [
            { label: "Dashboard-Übersicht", icon: LayoutDashboard, path: "/dashboard", testid: "menu-dashboard-overview" },
            { label: "Rufnummer-Erkennung", icon: PhoneIncoming, path: "/dealer/anrufe", testid: "menu-anrufe" },
            { label: "Partnerunternehmen", icon: Handshake, path: "/dealer/partner", testid: "menu-partner" },
            // Jun-2026 (User-Wunsch): Video-Beratung aus dem Menü in die Lead-Bearbeitung verschoben
            // (Button „Video" an jedem Lead) — Seite /dealer/videoberatung bleibt erreichbar.
            // Jul-2026 (User-Wunsch): KI-Mitbewerber-Beobachtung (5 Domains frei).
            { label: "Mitbewerber", icon: Eye, path: "/dealer/mitbewerber", testid: "menu-mitbewerber" },
            // Jul-2026 (User-Wunsch): Firmen-DNA — Philosophie, Werte & Leitfäden (KI-Kontext).
            { label: "Firmen-DNA & Leitfäden", icon: BookText, path: "/dealer/firmen-dna", testid: "menu-company-culture" },
            { label: "Standort-Postfach", icon: Mailbox, path: "/dealer/system-emails", testid: "menu-system-emails" },
            // Aug-2026 (User-Wunsch): Scan-Korrekturen (KI) von Werkstatt → Dashboard, nur Hauptsitz.
            { label: "Scan-Korrekturen (KI)", icon: Camera, path: "/dealer/scan-korrekturen", testid: "menu-scan-corrections" },
            { label: "E-Mail-Freigabe", icon: MailWarning, path: "/dealer/email-freigabe", testid: "menu-email-outbox", badgeKey: "email_outbox_pending" },
            { label: "Auswertungen", icon: BarChart3, path: "/dealer/analytics", testid: "menu-analytics" },
            { label: "Kosten & Nutzung", icon: Receipt, path: "/dealer/kosten", testid: "menu-usage-costs" },
        ],
    },
    // Jul-2026 (User-Wunsch) — neuer Hauptbereich „Verkaufen-Verkaufen-Verkaufen":
    // bündelt E-Mail-Center, Team-Chat, Leasingrückgabe & Finanzierungs-Ausläufe.
    {
        kind: "sub", label: "Verkauf", icon: BadgeEuro, triggerTestid: "menu-verkaufen-section", subWidth: "w-64",
        items: [
            { label: "E-Mail-Center", icon: Mail, path: "/dealer/email", testid: "menu-email-center", badgeKey: "email_unread" },
            { label: "Meine Leads", icon: Users, path: "/dealer/leads", testid: "menu-my-leads" },
            { label: "Dokumenten-Center", icon: Send, path: "/dealer/documents", testid: "menu-document-outbox" },
            { label: "Team-Chat", icon: MessagesSquare, path: "/dealer/team-chat", testid: "menu-team-chat" },
            { label: "Leasingrückgabe", icon: RotateCcw, path: "/dealer/leasingrueckgabe", testid: "menu-leasing-return" },
            { label: "Finanzierungs-Ausläufe", icon: Landmark, path: "/dealer/financing-expiries", testid: "menu-financing-expiries" },
        ],
    },
    {
        // Jul-2026 (User-Wunsch): Intranet als Aufklapp-Menü inkl. Dokumenten-Postausgang.
        kind: "sub", label: "Intranet", icon: Building2, triggerTestid: "menu-intranet", subWidth: "w-64",
        items: [
            { label: "Intranet-Übersicht", icon: Building2, path: "/intranet", testid: "menu-intranet-overview" },
            { label: "Dokumenten-Center", icon: Send, path: "/dealer/documents", testid: "menu-document-outbox-intranet" },
        ],
    },
    {
        kind: "sub", label: "Werkstatt", icon: Wrench, triggerTestid: "menu-workshop-orders", subWidth: "w-56",
        items: [
            { label: "Aufträge-Übersicht", icon: Wrench, path: "/dealer/workshop-orders", testid: "menu-workshop-orders-overview" },
            { label: "Werkstatt Kalender", icon: Wrench, path: "/dealer/workshop-calendar", testid: "menu-workshop-calendar" },
            { label: "Scan-Erfassung", icon: Camera, path: "/dealer/workshop-orders?action=zb1-scan", testid: "menu-workshop-zb1-scan" },
            { label: "Schnell Auftrag", icon: Zap, path: "/dealer/workshop-orders?action=quick-capture", testid: "menu-workshop-quick-capture" },
            { label: "Neuer Auftrag", icon: Plus, path: "/dealer/workshop-orders?action=new", testid: "menu-workshop-new-order" },
            { label: "Fahrzeuge", icon: Bike, path: "/dealer/invoices?tab=vehicles", testid: "menu-workshop-vehicles" },
            { label: "Kunden", icon: Users, path: "/dealer/invoices?tab=customers", testid: "menu-workshop-customers" },
            { label: "Neuer Kunde", icon: UserPlus, path: "/dealer/invoices?action=new-customer", testid: "menu-workshop-new-customer" },
            { label: "Bewertungen", icon: Wrench, path: "/dealer/workshop-reviews", testid: "menu-workshop-reviews" },
        ],
    },
    {
        kind: "sub", label: "Warenwirtschaft", icon: Package, triggerTestid: "menu-wms", subWidth: "w-56",
        items: [
            { label: "Warenwirtschaft-Übersicht", icon: Package, path: "/dealer/wms", testid: "menu-wms-overview" },
            // Aug-2026 (User-Wunsch): Reihenfolge — Lieferschein scannen direkt unter
            // der Übersicht, darunter Dokumenten-Posteingang, darunter Bestellungen.
            { label: "Lieferschein scannen", icon: FileText, path: "/dealer/wms/scan-delivery", testid: "menu-scan-delivery" },
            { label: "Dokumenten-Center", icon: FolderOpen, path: "/dealer/documents", testid: "menu-document-inbox" },
            { label: "Bestellungen", icon: Truck, path: "/dealer/purchase-orders", testid: "menu-purchase-orders" },
            { label: "Neuer Artikel", icon: Plus, path: "/dealer/wms?action=new-article", testid: "menu-new-article" },
            { label: "Barcode scannen", icon: ScanLine, path: "/dealer/wms?action=scan-barcode", testid: "menu-scan-barcode" },
            // Jul-2026 (User-Wunsch): Vorher/Nachher-Review der KI-Anreicherung + Etikettendruck.
            { label: "KI-Anreicherung prüfen & Etiketten", icon: ScanLine, path: "/dealer/wms/anreicherung", testid: "menu-wms-enrich-review" },
            { label: "Mein Onlineshop", icon: Store, onlineshop: true, testid: "menu-onlineshop" },
        ],
    },
    {
        kind: "sub", label: "Rechnungen", icon: FileText, triggerTestid: "menu-invoices", subWidth: "w-52",
        items: [
            { label: "Rechnungen-Übersicht", icon: FileText, path: "/dealer/invoices", testid: "menu-invoices-overview" },
            { label: "Neue Rechnung", icon: FileText, path: "/dealer/invoices?action=new-invoice", testid: "menu-new-invoice" },
            { label: "Direktverkauf", icon: Receipt, path: "/dealer/invoices?action=direct-sale", testid: "menu-direct-sale" },
            { label: "Kunden", icon: Users, path: "/dealer/invoices?tab=customers", testid: "menu-invoices-customers" },
            { label: "Fahrzeuge", icon: Bike, path: "/dealer/invoices?tab=vehicles", testid: "menu-invoices-vehicles" },
            { label: "Neuer Kunde", icon: UserPlus, path: "/dealer/invoices?action=new-customer", testid: "menu-invoices-new-customer" },
        ],
    },
    {
        kind: "sub", label: "Disposition", icon: Truck, triggerTestid: "menu-disposition", subWidth: "w-56",
        items: [
            { label: "Disposition-Übersicht", icon: Truck, path: "/dealer/disposition", testid: "menu-disposition-overview" },
            // Jul-2026 (User-Wunsch): Versand-Cockpit für ALLE Standorte im Header-Menü.
            { label: "Versand-Cockpit", icon: Send, path: "/dealer/versand", testid: "menu-versand-cockpit" },
            { label: "Transportplanung", icon: Truck, path: "/dealer/transport", testid: "menu-transport-planning" },
            { label: "Bestellung anlegen", icon: Plus, path: "/create-listing?mode=bestellung", testid: "menu-order-vehicle" },
            { label: "Bestellungen (Nachverfolgung)", icon: Truck, path: "/dealer/order-tracking", testid: "menu-order-tracking" },
            { label: "Fahrzeuginventur", icon: ClipboardCheck, path: "/dealer/fahrzeuginventur", testid: "menu-vehicle-inventory" },
            { label: "Fahrzeugpapier-Inventur", icon: Archive, path: "/dealer/papier-inventur", testid: "menu-paper-inventory" },
            { label: "Sammel-Scan Papiere", icon: ScanLine, path: "/dealer/disposition?action=papers-batch-scan", testid: "menu-papers-batch-scan" },
            // Jul-2026 (User-Wunsch): Bilddatenbank + Rote Nummer unter Disposition.
            { label: "Bilddatenbank", icon: Camera, path: "/dealer/bilddatenbank", testid: "menu-bilddatenbank" },
            { label: "Rote Nummer & Fahrtenbuch", icon: BookText, path: "/dealer/rote-nummer", testid: "menu-rote-nummer" },
        ],
    },
    {
        kind: "sub", label: "Buchhaltung", icon: Calculator, triggerTestid: "menu-buchhaltung", subWidth: "w-56",
        items: [
            { label: "Buchhaltung-Übersicht", icon: Receipt, path: "/dealer/buchhaltung", testid: "menu-buchhaltung-overview" },
            { label: "Kreditoren", icon: Receipt, path: "/dealer/buchhaltung?tab=kreditoren", testid: "menu-buchhaltung-kreditoren" },
            { label: "Zahlungsimport", icon: FileText, path: "/dealer/buchhaltung?tab=zahlungen", testid: "menu-buchhaltung-zahlungen" },
            { label: "EC-Belege (KI)", icon: ScanLine, path: "/dealer/buchhaltung?tab=ec-belege", testid: "menu-buchhaltung-ec-belege" },
            { label: "Provisionen", icon: Receipt, path: "/dealer/buchhaltung?tab=provisionen", testid: "menu-buchhaltung-provisionen" },
            { label: "Arbeitszeiten", icon: Calculator, path: "/dealer/buchhaltung?tab=arbeitszeiten", testid: "menu-buchhaltung-arbeitszeiten" },
            { label: "Lohnabrechnung", icon: BadgeEuro, path: "/dealer/lohn", testid: "menu-lohnabrechnung" },
            { label: "Bankimport", icon: Landmark, path: "/dealer/bank-import", testid: "menu-buchhaltung-bank" },
            { label: "DATEV-Export", icon: Calculator, path: "/dealer/buchhaltung?action=datev", testid: "menu-datev" },
        ],
    },
    { kind: "item", label: "Einstellungen", icon: SettingsIcon, path: "/dashboard?tab=settings", testid: "menu-settings" },
];

/**
 * Jul-2026 (Chef-Wunsch) — Sichtbare Menü-Sektionen je Rolle:
 * - Händler (b2b): alles.
 * - Feste Mitarbeiter (zweiradverkaeufer): volles Arbeitsmenü (Intranet,
 *   Werkstatt komplett, Warenwirtschaft, Rechnungen, Disposition, Leasing).
 *   Sensible Bereiche nur mit Leitungs-/Buchhaltungs-Modul: Buchhaltung,
 *   Auswertungen, Kosten & Nutzung. Einstellungen + Onlineshop nur Händler.
 */
const LEAD_MODULES = ["buchhaltung", "filialleitung", "geschaeftsfuehrung",
    "verkaufsleitung", "gebietsleitung", "bereichsleitung"];

// Jul-2026 (User-Wunsch) — diese Menüpunkte nur am HAUPTSITZ anzeigen
// (Filial-Konten und Filial-Mitarbeiter sehen sie nicht).
const HQ_ONLY_ITEMS = ["menu-company-culture", "menu-system-emails", "menu-anrufe", "menu-partner", "menu-mitbewerber", "menu-financing-expiries", "menu-scan-corrections"];

// Jul-2026 — diese Unterpunkte sehen feste Mitarbeiter nicht (vorher eigene
// Top-Level-Sektionen, jetzt im Bereich „Verkaufen-Verkaufen-Verkaufen").
const EMPLOYEE_HIDDEN_ITEMS = ["menu-email-center", "menu-leasing-return", "menu-financing-expiries"];

const applyHqOnly = (sections, user) => {
    const secs = (!user || user.is_hauptsitz_context !== false)
        ? sections
        : sections
            .filter((sec) => sec.kind !== "item" || !HQ_ONLY_ITEMS.includes(sec.testid))
            .map((sec) => sec.kind === "sub"
                ? { ...sec, items: sec.items.filter((it) => !HQ_ONLY_ITEMS.includes(it.testid)) }
                : sec);
    // Leere Untermenüs (alles ausgefiltert) komplett ausblenden.
    return secs.filter((sec) => sec.kind !== "sub" || sec.items.length > 0);
};

export const visibleDealerSections = (user) => {
    if (!user || user.role !== "zweiradverkaeufer") return applyHqOnly(DEALER_MENU_SECTIONS, user);
    const mods = Array.isArray(user.resolved_modules) ? user.resolved_modules : [];
    const isLead = mods.some((m) => LEAD_MODULES.includes(m));
    // Jul-2026 (User-Wunsch) — individuelle Menü-Sichtbarkeit aus der
    // Mitarbeiterverwaltung (Liste der ausgeblendeten Sektionen).
    const hidden = Array.isArray(user.menu_sections_hidden) ? user.menu_sections_hidden : [];
    return applyHqOnly(DEALER_MENU_SECTIONS
        .filter((sec) => {
            const key = sec.triggerTestid || sec.testid;
            if (hidden.includes(key)) return false;
            // Jul-2026 (User-Wunsch): Dashboard-Untermenü für Mitarbeiter
            // ausblenden (Dashboard-Link = Konto-Eintrag im Header).
            if (sec.triggerTestid === "menu-dashboard-section") return false;
            if (sec.testid === "menu-settings") return false;
            if (sec.triggerTestid === "menu-buchhaltung") return isLead;
            return true;
        })
        .map((sec) => {
            if (sec.kind !== "sub") return sec;
            // Jul-2026 (User-Wunsch) — auch einzelne UNTERPUNKTE ausblendbar;
            // E-Mail-Center/Leasingrückgabe/Finanzierungs-Ausläufe sehen
            // feste Mitarbeiter grundsätzlich nicht.
            let items = sec.items.filter((it) => !it.onlineshop && !hidden.includes(it.testid)
                && !EMPLOYEE_HIDDEN_ITEMS.includes(it.testid));
            if (isLead && sec.triggerTestid === "menu-buchhaltung") {
                // Auswertungen + Kosten (vorher Dashboard-Untermenü) für Leitung hier.
                const dash = DEALER_MENU_SECTIONS.find((s) => s.triggerTestid === "menu-dashboard-section");
                items = [...items, ...(dash?.items || []).filter((it) =>
                    ["menu-analytics", "menu-usage-costs"].includes(it.testid))];
            }
            // Aug-2026 (User-Wunsch) — Kundendienst: Werkstatt-Auswertung (nur
            // Werkstatt-Reiter) im Werkstatt-Menü freischalten.
            if (!isLead && user.employee_function === "kundendienst" && sec.triggerTestid === "menu-workshop-orders") {
                items = [...items, { label: "Werkstatt-Auswertung", icon: BarChart3, path: "/dealer/analytics?tab=workshop", testid: "menu-analytics-workshop" }];
            }
            return { ...sec, items };
        }), user);
};

/**
 * Baut die flache Mobile-Menü-Liste (B2B + feste Mitarbeiter) aus den je Rolle
 * sichtbaren Sektionen:
 * - item       → ein nicht eingerückter Eintrag.
 * - sub        → Parent (= Sektions-Label, Pfad der „Übersicht") + restliche
 *                Kinder eingerückt. Onlineshop wird als externer Link aufgelöst.
 */
export const buildDealerMobileLinks = (user) => {
    const links = [];
    for (const sec of visibleDealerSections(user)) {
        if (sec.kind === "item") {
            links.push({ label: sec.label, path: sec.path });
            continue;
        }
        const [overview, ...rest] = sec.items;
        links.push({ label: sec.label, path: overview.path });
        for (const it of rest) {
            if (it.onlineshop) links.push({ label: it.label, external: `/shop/${dealerSlug(user)}`, indent: true });
            else links.push({ label: it.label, path: it.path, indent: true });
        }
    }
    return links;
};
